import { useState, useEffect, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { X, Copy, Check, User, MapPin, TrendingUp, ChevronRight, ChevronDown } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { apiRequest } from "@/lib/queryClient";
import type { Player } from "../../../shared/schema";
import TournamentResultPN from "./tournament-result-pn";

// Vue Joueur (refonte Padel Now) — overlay plein écran au clic sur une ligne du classement.
// Overlay maison (pas Radix) : rendu/clic garantis, et fidèle à la fiche plein écran de Padel Now.
// i18n : libellés via t('playerSheet.*'). Dark : texte sur fond page en text-foreground (pn-navy réservé aux surfaces claires).

const ELO_TIERS = [1149, 1299, 1399, 1499, 1599, 1749, 1999];
const tierOf = (elo: number) => ELO_TIERS.findIndex((m) => elo <= m) + 1 || 8;

// Données FFT additionnelles passées depuis la liste FFT (âge, club non stockés sur le joueur PadELO).
type FftExtra = { idCrm?: string | null; prenom?: string | null; nom?: string | null; rank?: number | null; points?: number | null; evolution?: number | null; licence?: string | null; age?: number | string | null; club?: string | null } | null;

// Stats PadELO (endpoint /statistics) : position, meilleur partenaire, forme.
type PlayerStats = {
  left_win_rate: number; right_win_rate: number;
  all_time_best_partner_name: string | null; all_time_best_partner_matches: number | null; all_time_best_partner_win_rate: number | null;
  current_spree: number; best_spree: number; worst_spree: number;
};
const pctWin = (r?: number | null) => (r == null ? "0%" : `${Math.round(r * 100)}%`);
// Code couleur du rang (comme la liste principale) : or / argent / bronze pour le podium, sinon couleur de texte par défaut (lisible en dark).
const rankColor = (r?: number | null) => (r === 1 ? "text-pn-star" : r === 2 ? "text-slate-400" : r === 3 ? "text-amber-700" : "text-foreground");

type Competition = { competition_id: number; nom_tournoi: string | null; niveau: string | null; categorie: string | null; partenaire: string | null; rang_obtenu: number | null; points: number | null; expiration: string | null };
const frDate = (d: string | null) => { if (!d) return "—"; const x = new Date(d); return isNaN(x.getTime()) ? "—" : x.toLocaleDateString("fr-FR"); };
const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");
// categorie FFT = format du tableau (termes padel, non traduits).
const formatLabel = (cat: string | null) => ({ DM: "Double Messieurs", DD: "Double Dames", DX: "Double Mixte", MX: "Double Mixte", SM: "Simple Messieurs", SD: "Simple Dames" } as Record<string, string>)[cat ?? ""] || cat || "";
const NIVEAU_ORDER = ["P25", "P50", "P100", "P250", "P500", "P1000", "P1500", "P2000", "EQP1", "EQP2", "EQP3", "EQP4", "EQP5", "EQP6", "EQP8", "EQP10", "EQP11"];
const niveauIdx = (n: string | null) => { const i = NIVEAU_ORDER.indexOf(n ?? ""); return i < 0 ? 999 : i; };
const ordinal = (r: number) => (r === 1 ? "1er" : `${r}e`);
const medalBg = (r: number) => (r === 1 ? "#F5C518" : r === 2 ? "#C0C0C0" : "#CD7F32");

type Partner = { name: string; n: number; prenom: string | null; nom: string | null; rank: number | string | null };
// Mois restants avant expiration FFT (règle +1 mois : le tournoi reste valide le mois d'expiration).
const monthsUntil = (d: string | null): number | null => {
  if (!d) return null;
  const eff = new Date(d); if (isNaN(eff.getTime())) return null;
  eff.setMonth(eff.getMonth() + 1);
  return Math.round((eff.getTime() - Date.now()) / (1000 * 60 * 60 * 24 * 30.44));
};

const FlagFR = () => (
  <span className="inline-flex w-[18px] h-[13px] rounded-sm overflow-hidden border border-black/10 shrink-0">
    <i className="flex-1" style={{ background: "#0055A4" }} /><i className="flex-1 bg-white" /><i className="flex-1" style={{ background: "#EF4135" }} />
  </span>
);

function EloChart({ data }: { data: { eloRating: number }[] }) {
  const { t } = useTranslation();
  if (!data?.length) return <p className="text-center text-muted-foreground py-6">{t("ranking.stats.noHistory", "Pas encore d'historique")}</p>;
  const pts = [...data].reverse().map((d) => d.eloRating);
  const n = pts.length;
  const min = Math.min(...pts), max = Math.max(...pts), range = max - min || 100;
  const W = 320, H = 150, padX = 10, padT = 26, padB = 24;
  const x = (i: number) => padX + (i * (W - padX * 2)) / (n - 1 || 1);
  const y = (v: number) => padT + (H - padT - padB) - ((v - min) / range) * (H - padT - padB);
  const clampX = (xv: number) => Math.max(padX + 16, Math.min(W - padX - 16, xv));

  // Valeur courante seule : juste le point + le chiffre.
  if (n < 2) {
    return (
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
        <circle cx={W / 2} cy={H / 2} r="4" fill="var(--pn-blue)" />
        <text x={W / 2} y={H / 2 - 10} textAnchor="middle" fontSize="12" fontWeight="800" fill="var(--pn-blue)">{pts[0]}</text>
      </svg>
    );
  }

  const coords = pts.map((v, i) => ({ x: x(i), y: y(v) }));
  // Lissage monotone (Catmull-Rom → Bézier cubique).
  const smooth = (p: { x: number; y: number }[]) => {
    let d = `M ${p[0].x.toFixed(1)} ${p[0].y.toFixed(1)}`;
    for (let i = 0; i < p.length - 1; i++) {
      const p0 = p[i - 1] || p[i], p1 = p[i], p2 = p[i + 1], p3 = p[i + 2] || p2;
      const cp1x = p1.x + (p2.x - p0.x) / 6, cp1y = p1.y + (p2.y - p0.y) / 6;
      const cp2x = p2.x - (p3.x - p1.x) / 6, cp2y = p2.y - (p3.y - p1.y) / 6;
      d += ` C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
    }
    return d;
  };
  const line = smooth(coords);
  const area = `${line} L ${x(n - 1).toFixed(1)} ${H - padB} L ${x(0).toFixed(1)} ${H - padB} Z`;
  const maxI = pts.indexOf(max), minI = pts.indexOf(min), lastI = n - 1;
  // Évite le chevauchement avec le label de la valeur courante : on masque le label pic/creux s'il est trop proche du dernier point.
  const farFromLast = (i: number) => Math.abs(x(i) - x(lastI)) > 30;
  const showMaxLabel = maxI !== lastI && farFromLast(maxI);
  const showMinLabel = minI !== lastI && minI !== maxI && farFromLast(minI);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ fontFamily: "inherit" }}>
      <defs>
        <linearGradient id="eloGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--pn-blue)" stopOpacity="0.28" />
          <stop offset="100%" stopColor="var(--pn-blue)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#eloGrad)" />
      <path d={line} fill="none" stroke="var(--pn-blue)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

      {/* Marqueurs pic / creux (dot toujours ; label masqué s'il est confondu/trop proche du dernier point) */}
      {maxI !== lastI && <circle cx={x(maxI)} cy={y(max)} r="2.5" fill="var(--pn-blue)" />}
      {showMaxLabel && <text x={clampX(x(maxI))} y={y(max) - 7} textAnchor="middle" fontSize="10.5" fontWeight="600" fill="var(--muted-foreground)">{max}</text>}
      {minI !== lastI && minI !== maxI && <circle cx={x(minI)} cy={y(min)} r="2.5" fill="var(--pn-blue)" />}
      {showMinLabel && <text x={clampX(x(minI))} y={y(min) + 15} textAnchor="middle" fontSize="10.5" fontWeight="600" fill="var(--muted-foreground)">{min}</text>}

      {/* Dernier point = valeur actuelle */}
      <circle cx={x(lastI)} cy={y(pts[lastI])} r="4" fill="var(--pn-blue)" stroke="var(--background)" strokeWidth="2" />
      <text x={clampX(x(lastI))} y={y(pts[lastI]) - 10} textAnchor="middle" fontSize="12" fontWeight="800" fill="var(--pn-blue)">{pts[lastI]}</text>
    </svg>
  );
}

export default function PlayerSheetPN({ playerId, open, onClose, fft, onNavigateToClub, onOpenPlayer }: { playerId: string | null; open: boolean; onClose: () => void; fft?: FftExtra; onNavigateToClub?: (clubName: string) => void; onOpenPlayer?: (id: string) => void }) {
  const { t } = useTranslation();
  const { data: player, isLoading } = useQuery<Player>({
    queryKey: ["/api/players", playerId],
    queryFn: async () => (await apiRequest("GET", `/api/players/${playerId}`)).json(),
    enabled: !!playerId && open,
  });
  const { data: history = [] } = useQuery<{ eloRating: number }[]>({
    queryKey: ["/api/players", playerId, "elo-history", "pn"],
    queryFn: async () => (await apiRequest("GET", `/api/players/${playerId}/elo-history?limit=50&timeFilter=all`)).json(),
    enabled: !!playerId && open,
  });

  const [view, setView] = useState<"padelo" | "fft">("padelo");
  const [copied, setCopied] = useState(false);
  const [tri, setTri] = useState<"points" | "date" | "rang">("points");
  const [triOpen, setTriOpen] = useState(false);
  const [openComp, setOpenComp] = useState<number | null>(null);
  const [showUncounted, setShowUncounted] = useState(false);
  const [duoView, setDuoView] = useState<"avec" | "contre">("avec");
  const [duoAll, setDuoAll] = useState(false);
  const [duoTri, setDuoTri] = useState<"parties" | "pct">("parties");
  const [duoTriOpen, setDuoTriOpen] = useState(false);
  // Ouvert depuis la recherche FFT (idCrm fourni) → fiche FFT par défaut ; sinon PadELO.
  useEffect(() => { setView(fft?.idCrm ? "fft" : "padelo"); setCopied(false); setTri("points"); setOpenComp(null); setShowUncounted(false); setDuoView("avec"); setDuoAll(false); setDuoTri("parties"); setDuoTriOpen(false); }, [playerId, fft?.idCrm]);
  const copyLicence = (lic: string) => {
    try { navigator.clipboard.writeText(lic); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* clipboard indispo */ }
  };

  // Ouvert depuis la liste PadELO : pas d'idCrm, seulement la licence → on résout l'idCrm pour la fiche FFT complète.
  const { data: crm } = useQuery<{ idCrm: string | null; rank?: number | null; points?: number | null; evolution?: number | null; age?: number | null; club?: string | null }>({
    queryKey: ["/api/players", "fft-crm", player?.fftLicence],
    queryFn: async () => (await apiRequest("GET", `/api/players/fft-crm/${player!.fftLicence}`)).json(),
    enabled: open && view === "fft" && !fft?.idCrm && !!player?.fftLicence,
  });
  const idCrm = fft?.idCrm ?? crm?.idCrm ?? null;

  // Historique tournois FFT (Résultats + Tournois de la fiche) via idCrm.
  const { data: competitions = [] } = useQuery<Competition[]>({
    queryKey: ["/api/players", idCrm, "competitions"],
    queryFn: async () => {
      const r = await (await apiRequest("GET", `/api/players/${idCrm}/competitions`)).json();
      return Array.isArray(r) ? r : (r?.competitions ?? []);
    },
    enabled: open && view === "fft" && !!idCrm,
  });
  const { data: partners = [] } = useQuery<Partner[]>({
    queryKey: ["/api/players", idCrm, "partners"],
    queryFn: async () => {
      const r = await (await apiRequest("GET", `/api/players/${idCrm}/fft-partners`)).json();
      return r?.partners ?? [];
    },
    enabled: open && view === "fft" && !!idCrm,
  });

  // Stats PadELO (position/meilleur bro/forme) + clubs : réaffichés dans la vue PadELO (comme l'ancienne fiche).
  const { data: stats } = useQuery<PlayerStats>({
    queryKey: ["/api/players", playerId, "statistics", "pn"],
    queryFn: async () => (await apiRequest("GET", `/api/players/${playerId}/statistics`)).json(),
    enabled: !!playerId && open && view === "padelo",
  });
  const { data: clubs = [] } = useQuery<Array<{ id: string; name: string; isAdmin: boolean; rank: number | null }>>({
    queryKey: ["/api/players", playerId, "clubs", "pn"],
    queryFn: async () => (await apiRequest("GET", `/api/players/${playerId}/clubs`)).json(),
    enabled: !!playerId && open && view === "padelo",
  });
  // Classement global (id → rang) pour afficher le rang des partenaires/adversaires.
  const { data: rankings = [] } = useQuery<Array<{ id: string; rank: number }>>({
    queryKey: ["/api/players", "rankings", "all", "pn"],
    queryFn: async () => (await apiRequest("GET", `/api/players/rankings?timeFilter=all`)).json(),
    enabled: open && view === "padelo",
  });
  const rankById = new Map(rankings.map((r) => [r.id, r.rank]));
  // Partenaires (ratio de victoire avec) + adversaires (ratio de victoire contre), vue PadELO.
  const { data: duoPartners = [] } = useQuery<Array<{ partnerId: string; partnerName: string; matchesPlayed: number; matchesWon: number; winRate: number }>>({
    queryKey: ["/api/players", playerId, "partners", "pn"],
    queryFn: async () => (await apiRequest("GET", `/api/players/${playerId}/partners`)).json(),
    enabled: !!playerId && open && view === "padelo",
  });
  const { data: duoOpponents = [] } = useQuery<Array<{ opponentId: string; opponentName: string; matchesPlayed: number; matchesWon: number; winRate: number }>>({
    queryKey: ["/api/players", playerId, "opponents", "pn"],
    queryFn: async () => (await apiRequest("GET", `/api/players/${playerId}/opponents`)).json(),
    enabled: !!playerId && open && view === "padelo",
  });

  if (!open) return null;
  const winRate = player && player.matchesPlayed > 0 ? Math.round((player.matchesWon / player.matchesPlayed) * 100) : 0;
  const hasFft = !!player && (fft != null || player.fftRank != null || player.fftPoints != null);
  const displayName = player?.name || [fft?.prenom, fft?.nom].filter(Boolean).join(" ") || "—";
  // Joueur FFT non inscrit PadELO : pas de playerId → pas de bloc PadELO, fiche FFT seule.
  const waitingPlayer = !!playerId && (isLoading || !player);

  return (
    <div className="fixed inset-0 z-[70] bg-background overflow-y-auto font-body" role="dialog" aria-modal="true">
      <div className="flex items-center justify-between p-4 sticky top-0 bg-background z-10">
        <button onClick={onClose} aria-label={t("common.close", "Fermer")} className="w-11 h-11 rounded-full bg-muted border border-border flex items-center justify-center text-foreground">
          <X className="w-5 h-5" />
        </button>
        <h2 className="font-head font-extrabold text-xl text-foreground">{t("playerSheet.title", "Joueur")}</h2>
        <span className="w-11" />
      </div>

      {waitingPlayer ? (
        <div className="p-6 space-y-4"><Skeleton className="h-56 w-full rounded-3xl" /><Skeleton className="h-28 w-full rounded-3xl" /></div>
      ) : (
        <div className="p-4 pb-10">
          <h3 className="text-center font-head font-extrabold text-2xl text-foreground mt-2">{displayName}</h3>

          {hasFft && (
            <div className="flex bg-pn-navy rounded-full p-1.5 gap-1 mt-4 font-head">
              {(["padelo", "fft"] as const).map((v) => (
                <button key={v} onClick={() => setView(v)}
                  className={`flex-1 py-2.5 rounded-full font-bold transition ${view === v ? "bg-white text-pn-navy" : "text-white"}`}>
                  {v === "padelo" ? "PadELO" : "FFT"}
                </button>
              ))}
            </div>
          )}

          {view === "padelo" && player ? (
            <>
              <div className="rounded-3xl bg-pn-navy text-white text-center py-6 px-4 mt-4">
                <div className="font-head font-extrabold text-5xl leading-none">{player.eloRating}</div>
                <div className="opacity-90 mt-1">{t("playerSheet.pointsElo", "Points ELO")}</div>
                <span className="inline-block mt-3 border border-white/40 rounded-full px-4 py-1.5 font-semibold">{t("playerSheet.level", "Niveau {{n}} / 8", { n: tierOf(player.eloRating) })}</span>
              </div>
              <div className="text-center my-4"><span className="bg-pn-lime text-pn-navy font-bold text-xs px-3 py-1.5 rounded-full">{t("playerSheet.evolutionElo", "Évolution ELO")}</span></div>
              <div className="rounded-3xl border border-border p-4"><EloChart data={history} /></div>

              <div className="text-center my-4"><span className="bg-pn-lime text-pn-navy font-bold text-xs px-3 py-1.5 rounded-full">{t("playerSheet.stats", "Stats")}</span></div>
              <div className="grid grid-cols-3 gap-2.5">
                <Pill label={t("playerSheet.wins", "Victoires")}>{player.matchesWon}</Pill>
                <Pill label={t("playerSheet.winRate", "Win rate")}>{winRate}%</Pill>
                <Pill label={t("playerSheet.matches", "Matchs")}>{player.matchesPlayed}</Pill>
              </div>

              {stats && (
                <div className="grid grid-cols-2 gap-2.5 mt-4">
                  {/* Victoires par position */}
                  <div className="bg-pn-blue-soft rounded-2xl p-3.5 flex flex-col">
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t("playerSheet.winsPosition", "Victoires position")}</div>
                    <div className="flex gap-2 mt-auto">
                      {[[t("playerSheet.left", "Gau."), stats.left_win_rate], [t("playerSheet.right", "Dro."), stats.right_win_rate]].map(([l, r]) => (
                        <div key={l as string} className="flex-1 bg-white rounded-xl py-2 text-center">
                          <div className="text-[10px] text-muted-foreground uppercase font-medium">{l}</div>
                          <div className="font-head font-extrabold text-pn-navy">{pctWin(r as number)}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Meilleur bro */}
                  <div className="bg-pn-blue-soft rounded-2xl p-3.5 flex flex-col">
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t("playerSheet.bestBro", "Meilleur bro")}</div>
                    <div className="flex-1 flex flex-col justify-center text-center">
                      <div className="font-head font-extrabold text-lg text-pn-navy truncate">
                        {stats.all_time_best_partner_name ? stats.all_time_best_partner_name.split(" ")[0] : "—"}
                      </div>
                      {stats.all_time_best_partner_name && (
                        <div className="text-[11px] text-pn-blue font-medium mt-0.5">
                          {t("playerSheet.broStat", "{{m}} matchs • {{pct}}", { m: stats.all_time_best_partner_matches, pct: pctWin(stats.all_time_best_partner_win_rate) })}
                        </div>
                      )}
                    </div>
                  </div>

                </div>
              )}

              {stats && (
                <>
                  <div className="text-center my-4"><span className="bg-pn-lime text-pn-navy font-bold text-xs px-3 py-1.5 rounded-full">{t("playerSheet.form", "Forme")}</span></div>
                  <div className="rounded-3xl bg-pn-navy text-white flex py-5">
                    {([
                      [t("playerSheet.formMin", "Min"), `-${stats.worst_spree}`],
                      [t("playerSheet.formCurrent", "Actuelle"), (stats.current_spree > 0 ? "+" : "") + stats.current_spree],
                      [t("playerSheet.formMax", "Max"), `+${stats.best_spree}`],
                    ] as const).map(([l, v], i) => (
                      <div key={l} className={`flex-1 text-center ${i ? "border-l border-white/20" : ""}`}>
                        <div className="font-head font-extrabold text-xl">{v}</div>
                        <div className="opacity-85 text-sm">{l}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {clubs.length > 0 && (
                <>
                  <div className="text-center my-4"><span className="bg-pn-lime text-pn-navy font-bold text-xs px-3 py-1.5 rounded-full">{t("playerSheet.clubs", "Clubs")}</span></div>
                  <div className="space-y-3">
                    {clubs.map((c) => (
                      <button key={c.id} onClick={() => { onNavigateToClub?.(c.name); onClose(); }}
                        className="w-full text-left rounded-2xl border border-border p-3.5 flex items-center gap-3 active:scale-[.99] transition">
                        <div className={`font-head font-extrabold text-2xl min-w-[44px] text-center shrink-0 ${rankColor(c.rank)}`}>#{c.rank ?? "-"}</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-foreground truncate">{c.name}</div>
                          {c.isAdmin && <div className="text-sm text-pn-blue font-medium">{t("playerSheet.admin", "Administrateur")} ★</div>}
                        </div>
                        <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
                      </button>
                    ))}
                  </div>
                </>
              )}

              {(duoPartners.length > 0 || duoOpponents.length > 0) && (() => {
                const raw = duoView === "avec"
                  ? duoPartners.map((p) => ({ id: p.partnerId, name: p.partnerName, played: p.matchesPlayed, won: p.matchesWon, rate: p.winRate }))
                  : duoOpponents.map((o) => ({ id: o.opponentId, name: o.opponentName, played: o.matchesPlayed, won: o.matchesWon, rate: o.winRate }));
                const rows = [...raw].sort((a, b) => duoTri === "pct" ? b.rate - a.rate || b.played - a.played : b.played - a.played || b.rate - a.rate);
                const shown = duoAll ? rows : rows.slice(0, 5);
                const duoTriLabel = duoTri === "pct" ? t("playerSheet.sortPct", "pourcentage") : t("playerSheet.sortMatches", "nombre de parties");
                return (
                  <>
                    <div className="text-center my-4"><span className="bg-pn-lime text-pn-navy font-bold text-xs px-3 py-1.5 rounded-full">{t("playerSheet.partnersOpponents", "Partenaires & adversaires")}</span></div>
                    <div className="flex bg-pn-navy rounded-full p-1.5 gap-1 mb-4 font-head">
                      {([["avec", t("playerSheet.with", "Avec")], ["contre", t("playerSheet.against", "Contre")]] as const).map(([v, l]) => (
                        <button key={v} onClick={() => { setDuoView(v as "avec" | "contre"); setDuoAll(false); }}
                          className={`flex-1 py-2.5 rounded-full font-bold transition ${duoView === v ? "bg-white text-pn-navy" : "text-white"}`}>{l}</button>
                      ))}
                    </div>
                    <div className="relative mb-3">
                      <button onClick={() => setDuoTriOpen((o) => !o)} className="inline-flex items-center gap-2 border border-border rounded-full px-4 py-2.5 font-semibold text-foreground">
                        {t("playerSheet.sort", "Tri")} : <b className="text-pn-blue">{duoTriLabel}</b> <span className="text-xs">▾</span>
                      </button>
                      {duoTriOpen && (
                        <div className="absolute z-10 mt-1 bg-background border border-border rounded-xl shadow-lg overflow-hidden min-w-[180px]">
                          {([["parties", t("playerSheet.sortMatches", "nombre de parties")], ["pct", t("playerSheet.sortPct", "pourcentage")]] as const).map(([k, lbl]) => (
                            <button key={k} onClick={() => { setDuoTri(k as "parties" | "pct"); setDuoTriOpen(false); }}
                              className={`block w-full text-left px-4 py-3 font-semibold ${k === duoTri ? "text-pn-blue bg-pn-blue-soft" : "text-foreground"}`}>
                              {lbl}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {shown.length === 0 ? (
                      <p className="text-center text-muted-foreground py-4">{duoView === "avec" ? t("playerSheet.noPartner", "Aucun partenaire") : t("playerSheet.noOpponent", "Aucun adversaire")}</p>
                    ) : (
                      <div className="space-y-3">
                        {shown.map((r) => (
                          <button key={r.id} onClick={() => onOpenPlayer?.(r.id)}
                            className="w-full text-left rounded-2xl border border-border p-3.5 flex items-center gap-3 active:scale-[.99] transition">
                            <div className={`font-head font-extrabold text-2xl min-w-[44px] text-center shrink-0 ${rankColor(rankById.get(r.id))}`}>#{rankById.get(r.id) ?? "-"}</div>
                            <div className="flex-1 min-w-0">
                              <div className="font-bold text-foreground truncate">{r.name}</div>
                              <div className="text-sm text-muted-foreground">{t("playerSheet.duoMatches", "{{won}}/{{played}} matchs", { won: r.won, played: r.played })}</div>
                            </div>
                            <span className="bg-pn-lime text-pn-navy font-bold text-xs rounded-full px-2.5 py-0.5 shrink-0">{t("playerSheet.winBadge", "{{rate}}% V", { rate: r.rate })}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {rows.length > 5 && (
                      <button onClick={() => setDuoAll((o) => !o)}
                        className="w-full mt-3 rounded-2xl border border-border py-3.5 flex items-center justify-center gap-2 font-bold text-foreground">
                        {duoAll ? t("playerSheet.seeLess", "Voir moins") : t("playerSheet.seeAll", "Voir les {{n}}", { n: rows.length })}
                        <ChevronDown className={`w-4 h-4 transition-transform ${duoAll ? "rotate-180" : ""}`} />
                      </button>
                    )}
                  </>
                );
              })()}
            </>
          ) : (() => {
            const fRank = fft?.rank ?? crm?.rank ?? player?.fftRank;
            const fPoints = fft?.points ?? crm?.points ?? player?.fftPoints;
            const fEvo = fft?.evolution ?? crm?.evolution ?? player?.fftEvolution;
            const fLic = fft?.licence ?? player?.fftLicence;
            const fAgeRaw = fft?.age ?? crm?.age;
            const fAge = fAgeRaw != null ? Number(fAgeRaw) : null;
            const fVille = fft?.club || crm?.club || null;
            return (
              <>
                <div className="rounded-3xl bg-pn-navy text-white text-center py-6 px-4 mt-4">
                  <div className="font-head font-extrabold text-5xl leading-none">{fRank != null ? `#${fRank}` : "—"}</div>
                  <div className="opacity-90 mt-1">{t("playerSheet.franceRanking", "Classement France")}</div>
                </div>
                <div className="flex gap-2.5 mt-4">
                  {fAge != null && !isNaN(fAge) && <FftPill><User className="w-4 h-4 text-pn-navy" />{t("playerSheet.years", "{{n}} ans", { n: fAge })}</FftPill>}
                  <FftPill><FlagFR /> FRA</FftPill>
                  {fPoints != null && <FftPill><TrendingUp className="w-4 h-4 text-pn-navy" />{fPoints} pts</FftPill>}
                </div>
                {(fVille || fLic) && (
                  <div className="flex gap-2.5 mt-2.5">
                    {fVille && <FftPill><MapPin className="w-4 h-4 text-pn-navy" />{fVille}</FftPill>}
                    {fLic && (
                      <button onClick={() => copyLicence(fLic)} className="flex-1 min-w-0 bg-pn-blue-soft rounded-2xl py-3.5 px-2 flex items-center justify-center gap-2 text-pn-navy font-semibold">
                        {copied ? <Check className="w-4 h-4 text-pn-green" /> : <Copy className="w-4 h-4 text-pn-blue" />}
                        <span className="truncate">{copied ? t("playerSheet.copied", "Copié") : `#${fLic}`}</span>
                      </button>
                    )}
                  </div>
                )}
                {fEvo != null && fEvo !== 0 && (
                  <p className={`text-center mt-3 font-semibold ${fEvo > 0 ? "text-green-600" : "text-red-500"}`}>{fEvo > 0 ? `▲ ${fEvo}` : `▼ ${Math.abs(fEvo)}`} {t("playerSheet.places", "places")}</p>
                )}

                {competitions.length > 0 && (() => {
                  // FFT ne compte que les 12 meilleurs résultats (par points) : médailles + barres + stats basés sur eux.
                  const byPoints = [...competitions].sort((a, b) => (b.points ?? -1) - (a.points ?? -1));
                  const counted = byPoints.slice(0, 12);
                  const countedIds = new Set(counted.map((c) => c.competition_id));
                  const best = counted[0] ?? null;
                  const bestLabel = best ? `${best.rang_obtenu != null ? `#${best.rang_obtenu} ` : ""}${best.niveau ?? ""}`.trim() || "—" : "—";
                  const partnersCount = partners.length || new Set(counted.map((c) => c.partenaire).filter(Boolean)).size;

                  // Niveaux triés plus grande catégorie d'abord (P1000 avant P500 avant P250).
                  const niveaux = Array.from(new Set(counted.map((c) => c.niveau).filter(Boolean) as string[])).sort((a, b) => niveauIdx(b) - niveauIdx(a));
                  const byNiveau = niveaux.map((niv) => {
                    const comps = counted.filter((c) => c.niveau === niv);
                    const rc: Record<number, number> = {};
                    comps.forEach((c) => { if (c.rang_obtenu != null) rc[c.rang_obtenu] = (rc[c.rang_obtenu] || 0) + 1; });
                    // Seulement les 4 meilleures places (rang le plus bas) pour tenir sur une ligne.
                    const ranks = Object.entries(rc).map(([r, n]) => ({ r: +r, n })).sort((a, b) => a.r - b.r).slice(0, 4);
                    return { niv, total: comps.length, ranks };
                  });
                  const ptsByNiveau = niveaux.map((niv) => ({ niv, pts: counted.filter((c) => c.niveau === niv).reduce((s, c) => s + (c.points || 0), 0) }))
                    .sort((a, b) => niveauIdx(b.niv) - niveauIdx(a.niv));
                  const maxPts = Math.max(1, ...ptsByNiveau.map((p) => p.pts));
                  const totalPts = ptsByNiveau.reduce((s, p) => s + p.pts, 0);

                  const sortFn = (a: Competition, b: Competition) => {
                    if (tri === "points") return (b.points ?? -1) - (a.points ?? -1);
                    if (tri === "rang") return (a.rang_obtenu ?? 1e9) - (b.rang_obtenu ?? 1e9);
                    return new Date(b.expiration ?? 0).getTime() - new Date(a.expiration ?? 0).getTime();
                  };
                  const countedSorted = [...counted].sort(sortFn);
                  const uncounted = competitions.filter((c) => !countedIds.has(c.competition_id)).sort(sortFn);
                  const triLabel = tri === "points" ? t("playerSheet.sortPoints", "points gagnés") : tri === "date" ? t("playerSheet.sortDate", "date") : t("playerSheet.sortRank", "rang");

                  return (
                    <>
                      {/* Partenaires */}
                      {partners.length > 0 && (
                        <>
                          <div className="text-center my-4"><span className="bg-pn-lime text-pn-navy font-bold text-xs px-3 py-1.5 rounded-full">{t("playerSheet.partners", "Partenaires")}</span></div>
                          <div className="flex gap-3 overflow-x-auto pt-2 pb-4 [&::-webkit-scrollbar]:hidden">
                            {partners.map((p) => (
                              <div key={p.name} className="shrink-0 w-[92px] flex flex-col items-center text-center">
                                <div className="relative">
                                  <div className="w-16 h-16 rounded-full bg-pn-blue-soft text-pn-blue font-head font-bold text-lg flex items-center justify-center">{initials(p.prenom && p.nom ? `${p.prenom} ${p.nom}` : p.name)}</div>
                                  <span className="absolute -top-1 -right-1 bg-pn-blue text-white text-xs font-bold px-1.5 py-0.5 rounded-full">x{p.n}</span>
                                  {p.rank != null && <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-pn-lime text-pn-navy text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap">#{Number(p.rank).toLocaleString("fr-FR").replace(/ /g, " ")}</span>}
                                </div>
                                <div className="text-xs text-foreground mt-3 leading-tight line-clamp-2 break-words">{p.prenom || p.name}</div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}

                      {/* Résultats */}
                      <div className="text-center my-4"><span className="bg-pn-lime text-pn-navy font-bold text-xs px-3 py-1.5 rounded-full">{t("playerSheet.results", "Résultats")}</span></div>
                      <div className="rounded-3xl bg-pn-navy text-white flex py-5 mb-4">
                        {[[competitions.length, t("playerSheet.tournaments", "Tournois")], [bestLabel, t("playerSheet.bestPerf", "Meilleure perf")], [partnersCount, t("playerSheet.partners", "Partenaires")]].map(([v, l], i) => (
                          <div key={l as string} className={`flex-1 text-center ${i ? "border-l border-white/20" : ""}`}>
                            <div className="font-head font-extrabold text-xl">{v}</div>
                            <div className="opacity-85 text-sm">{l}</div>
                          </div>
                        ))}
                      </div>

                      {/* Médailles par niveau */}
                      <div className="space-y-3">
                        {byNiveau.map((g) => (
                          <div key={g.niv} className="flex items-center gap-2.5 flex-nowrap overflow-hidden border-b border-border/60 pt-2 pb-3">
                            <span className="relative bg-pn-blue-soft text-pn-blue font-bold text-sm rounded-full px-3 py-1">
                              {g.niv}
                              <span className="absolute -top-2 -right-2 bg-pn-blue text-white text-[10px] font-bold px-1.5 rounded-full">x{g.total}</span>
                            </span>
                            {g.ranks.map(({ r, n }) => (
                              <span key={r} className="inline-flex items-center gap-1.5 font-bold text-foreground">
                                {n} x
                                {r <= 3
                                  ? <span style={{ background: medalBg(r) }} className="w-7 h-7 rounded-full text-white text-xs flex items-center justify-center">{r}</span>
                                  : <span className="bg-muted text-foreground rounded-full px-2.5 py-1 text-sm">{ordinal(r)}</span>}
                              </span>
                            ))}
                          </div>
                        ))}
                      </div>

                      {/* Points par catégorie */}
                      <div className="flex items-center justify-between mt-5 mb-3">
                        <h4 className="font-head font-bold text-foreground">{t("playerSheet.pointsByCategory", "Points par catégorie")}</h4>
                        <span className="text-muted-foreground font-semibold">{totalPts.toLocaleString("fr-FR").replace(/ /g, " ")} pts</span>
                      </div>
                      <div className="space-y-2.5">
                        {ptsByNiveau.map((p) => (
                          <div key={p.niv} className="flex items-center gap-3">
                            <span className="bg-pn-blue-soft text-pn-blue font-bold text-xs rounded-full px-3 py-1 min-w-[62px] text-center">{p.niv}</span>
                            <div className="flex-1 h-4 bg-pn-blue-soft rounded-full overflow-hidden">
                              <div className="h-full bg-pn-navy rounded-full" style={{ width: `${Math.max(6, (p.pts / maxPts) * 100)}%` }} />
                            </div>
                            <span className="font-bold text-foreground text-sm min-w-[72px] text-right">{p.pts.toLocaleString("fr-FR").replace(/ /g, " ")} pts</span>
                          </div>
                        ))}
                      </div>

                      {/* Tournois */}
                      <div className="text-center my-4"><span className="bg-pn-lime text-pn-navy font-bold text-xs px-3 py-1.5 rounded-full">{t("playerSheet.tournaments", "Tournois")}</span></div>
                      <div className="relative mb-3">
                        <button onClick={() => setTriOpen((o) => !o)} className="inline-flex items-center gap-2 border border-border rounded-full px-4 py-2.5 font-semibold text-foreground">
                          {t("playerSheet.sort", "Tri")} : <b className="text-pn-blue">{triLabel}</b> <span className="text-xs">▾</span>
                        </button>
                        {triOpen && (
                          <div className="absolute z-10 mt-1 bg-background border border-border rounded-xl shadow-lg overflow-hidden min-w-[180px]">
                            {([["points", t("playerSheet.sortPoints", "points gagnés")], ["date", t("playerSheet.sortDate", "date")], ["rang", t("playerSheet.sortRank", "rang")]] as const).map(([k, lbl]) => (
                              <button key={k} onClick={() => { setTri(k as "points" | "date" | "rang"); setTriOpen(false); }}
                                className={`block w-full text-left px-4 py-3 font-semibold ${k === tri ? "text-pn-blue bg-pn-blue-soft" : "text-foreground"}`}>
                                {lbl}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="space-y-3">
                        {countedSorted.map((c) => <CompRow key={c.competition_id} c={c} onOpen={setOpenComp} />)}
                      </div>

                      {uncounted.length > 0 && (
                        <>
                          <button onClick={() => setShowUncounted((o) => !o)}
                            className="w-full mt-3 rounded-2xl border border-border py-3.5 flex items-center justify-center gap-2 font-bold text-foreground">
                            {showUncounted ? t("playerSheet.hideUncounted", "Masquer les non comptabilisés") : t("playerSheet.uncountedTournaments", "Tournois non comptabilisés")}
                            <ChevronDown className={`w-4 h-4 transition-transform ${showUncounted ? "rotate-180" : ""}`} />
                          </button>
                          {showUncounted && (
                            <div className="space-y-3 mt-3">
                              {uncounted.map((c) => <CompRow key={c.competition_id} c={c} onOpen={setOpenComp} />)}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  );
                })()}
              </>
            );
          })()}
        </div>
      )}

      {openComp != null && <TournamentResultPN competitionId={openComp} onClose={() => setOpenComp(null)} />}
    </div>
  );
}

// Ligne tournoi (réutilisée pour comptabilisés + non comptabilisés).
function CompRow({ c, onOpen }: { c: Competition; onOpen: (id: number) => void }) {
  const { t } = useTranslation();
  const m = monthsUntil(c.expiration);
  const soon = m != null && m <= 2;
  return (
    <button onClick={() => onOpen(c.competition_id)}
      className="w-full text-left rounded-2xl border border-border p-4 flex items-center gap-4 active:scale-[.99] transition">
      <div className="min-w-[56px]">
        {c.rang_obtenu != null && <div className="font-head font-extrabold text-2xl text-foreground">#{c.rang_obtenu}</div>}
        {c.points != null && <div className="mt-1.5 bg-pn-green-soft text-pn-green font-bold text-xs rounded-full px-2.5 py-0.5 text-center">+{c.points} pts</div>}
        {c.niveau && <div className="mt-1.5 bg-pn-blue-soft text-pn-blue font-bold text-xs rounded-full px-2.5 py-0.5 text-center">{c.niveau}</div>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-foreground leading-snug">{c.nom_tournoi ?? "—"}</div>
        {c.categorie && <div className="text-muted-foreground text-sm mt-0.5">{formatLabel(c.categorie)}</div>}
        <div className={`text-sm mt-1 font-medium ${soon ? "text-red-500" : "text-muted-foreground"}`}>
          {m != null && m <= 0 ? t("playerSheet.expiredOn", "Expiré le ") : t("playerSheet.validUntil", "Validité : ")}{frDate(c.expiration)}
        </div>
      </div>
      <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
    </button>
  );
}

function Pill({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="bg-pn-blue-soft rounded-2xl py-3.5 px-2 text-center">
      <div className="font-head font-extrabold text-xl text-pn-navy">{children}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

// Pill style fiche FFT Padel Now : icône + valeur sur une ligne.
function FftPill({ children }: { children: ReactNode }) {
  return (
    <div className="flex-1 min-w-0 bg-pn-blue-soft rounded-2xl py-3.5 px-2 flex items-center justify-center gap-2 font-semibold text-pn-navy whitespace-nowrap overflow-hidden">
      {children}
    </div>
  );
}
